import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  lastValueFrom,
  map,
  of,
  shareReplay,
  throwError,
} from 'rxjs';
import { ProxyEndPoints } from '../config/endpoints';
import { IntakeFormFields } from '../models/intake-form.model';
import {
  Api_Status,
  CancelVendorOrderCommand,
  ContactAzTeamData,
  ContactAzTeamNotificationType,
  Estimate,
  FuzzyBreakdown,
  VendorJobDetails,
  VendorOrder,
  VendorOrderUpdate,
} from '../models/vendor-order.model';
import config from '../../../config';
import { Language, LanguagesTranslationObject } from '../models/language.model';
import { TranslateService as NgxTranslateService } from '@ngx-translate/core';

const PROXY_URL = () => `${config.API_ENDPOINT}`;

/**
 * Service for vendor translation quote operations.
 * Handles intake form submission, vendor order CRUD, and vendor availability.
 */
@Injectable({ providedIn: 'root' })
export class VendorService {
  private readonly http = inject(HttpClient);
  private readonly ngxTranslate = inject(NgxTranslateService);

  /**
   * create a vendor order and send files into Smartling to retrieve estimates (job not authorized)
   * Takes inputs and returns the json response
   * @returns json response
   */
  sendIntakeFormRequest(
    data: IntakeFormFields,
    isCanadianUser = false,
  ): Observable<any> {
    const queryParams = isCanadianUser ? '?canadian=y' : '';
    const url = `${PROXY_URL()}${ProxyEndPoints.vendorOrders}${queryParams}`;
    return this.http.post<any>(url, data);
  }

  /**
   * Cancel a vendor order by job UID.
   * Supports both legacy format (jobUid, reason) and new format with full request object.
   */
  cancelVendorOrder(
    jobUidOrRequest: string | Record<string, unknown>,
    reason?: string,
  ): Observable<void> {
    let payload: Record<string, unknown>;

    // Handle new request format (object with jobUid, filesArray, cancelAll, etc.)
    if (jobUidOrRequest !== null && typeof jobUidOrRequest === 'object') {
      payload = { ...jobUidOrRequest };
      if (payload['reason'] && typeof payload['reason'] === 'string') {
        payload['reason'] = payload['reason'].trim();
      }
      if (payload['cancelAll'] === undefined) {
        payload['cancelAll'] = true;
      }
    } else {
      // Handle legacy format (jobUid: string, reason?: string)
      const trimmedReason = reason?.trim();
      payload = {
        jobUid: jobUidOrRequest,
        cancelAll: true,
        ...(trimmedReason ? { reason: trimmedReason } : {}),
      };
    }

    return this.http
      .patch<void>(
        `${PROXY_URL()}${ProxyEndPoints.vendorOrdersCancelNew}`,
        payload,
      )
      .pipe(shareReplay());
  }

  /**
   * Cancel specific files in a vendor order (TLS-2237).
   * @param request - Cancellation request with jobUid, filesArray (composite key entries), jobName, vendorName, email, vendorEmail, cancelAll, and optional reason
   * @returns Observable with cancellation response including metrics
   */
  cancelVendorOrderSpecificFiles(request: {
    jobUid: string;
    filesArray: { id: string; translatedDate?: number; filename?: string; language?: string }[];
    jobName?: string;
    vendorName?: string;
    email?: string;
    vendorEmail?: string;
    cancelAll?: boolean;
    reason?: string;
  }): Observable<any> {
    const payload: Record<string, unknown> = {
      jobUid: request.jobUid,
      filesArray: request.filesArray,
    };

    if (request.jobName) {
      payload['jobName'] = request.jobName;
    }

    if (request.vendorName) {
      payload['vendorName'] = request.vendorName;
    }

    if (request.email) {
      payload['email'] = request.email;
    }

    if (request.vendorEmail) {
      payload['vendorEmail'] = request.vendorEmail;
    }

    if (request.cancelAll !== undefined) {
      payload['cancelAll'] = request.cancelAll;
    }

    if (request.reason) {
      payload['reason'] = request.reason.trim();
    }

    return this.http
      .patch<any>(
        `${PROXY_URL()}${ProxyEndPoints.vendorOrdersCancelNew}`,
        payload,
      )
      .pipe(shareReplay());
  }

  async getLocales(requiredLocales: string): Promise<Language[]> {
    const languagesTranslationObject = await this.getLanguagesTranslated();

    return lastValueFrom(
      this.http
        .get<
          Language[]
        >(`${PROXY_URL()}${ProxyEndPoints.pathGetLanguages}?requestedLocales=${requiredLocales}`)
        .pipe(
          map((response) =>
            response.map((language: Language) => {
              language.name =
                languagesTranslationObject[language.code] ?? language.name;
              return language;
            }),
          ),
          catchError(() => of([])),
        ),
    );
  }

  /**
   * Get language code → translated name map from i18n JSON.
   */
  private getLanguagesTranslated(): Promise<LanguagesTranslationObject> {
    return lastValueFrom(this.ngxTranslate.get('languages'));
  }

  // ── Vendor availability ────────────────────────────────────────────────

  getAvailableVendors(): Observable<any[]> {
    return this.http.get<any[]>(
      `${PROXY_URL()}${ProxyEndPoints.vendorsAvailable}`,
    );
  }

  // ── Vendor orders CRUD ─────────────────────────────────────────────────

  /**
   * Load all vendor orders, optionally filtering for video-only jobs.
   */
  loadAllVendorOrders(showVideosOnly = false): Observable<VendorOrder[]> {
    const url = `${PROXY_URL()}${ProxyEndPoints.vendorOrders}?showVideosOnly=${showVideosOnly}`;
    return this.http.get<VendorOrder[]>(url).pipe(
      map((responseData) => responseData),
      shareReplay(),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  /**
   * Get detailed job information for a specific vendor order.
   */
  getJobDetails(
    jobUid: string,
    vendorName: string,
    status: Api_Status,
  ): Promise<VendorJobDetails[]> {
    const url = `${PROXY_URL()}${ProxyEndPoints.vendorOrders}/jobDetail`;
    const params = new HttpParams()
      .set('jobUid', jobUid)
      .append('vendorName', vendorName)
      .append('jobStatus', status);

    return lastValueFrom(this.http.get<VendorJobDetails[]>(url, { params }));
  }

  /**
   * Select a vendor for a specific order (PATCH).
   */
  selectVendorOrder(jobUid: string, data: VendorOrderUpdate): Observable<any> {
    return this.http
      .patch<void>(
        `${PROXY_URL()}${ProxyEndPoints.vendorOrders}/${jobUid}/vendor`,
        data,
      )
      .pipe(shareReplay());
  }

  /**
   * Authorize a vendor order (PATCH).
   * purchaseOrderId is sent as a raw JSON number to avoid JavaScript Number precision loss for large values.
   */
  authorizeVendorOrder(
    jobUid: string,
    data: {
      purchaseOrderId: string;
      deliveryTypeAcceptance: 'standard' | 'rushed';
      rushedDateSelected: boolean;
      vendorPrice?: number;
      deadline?: string;
      vendorId?: string;
      vendorName?: string;
      jobCreatedIn?: string;
    },
  ): Observable<any> {
    return this.http
      .patch<any>(
        `${PROXY_URL()}${ProxyEndPoints.vendorOrders}/${jobUid}/authorize`,
        data,
      )
      .pipe(shareReplay());
  }

  // ── Estimates & Breakdowns ─────────────────────────────────────────────

  /**
   * Get Smartling estimate report breakdown.
   */
  getEstimateReportBreakdown(
    contentType: string,
    sourceLocale: string,
    targetLocale: string,
    estimationsId: string,
  ): Promise<FuzzyBreakdown> {
    const url =
      `${PROXY_URL()}${ProxyEndPoints.estimateReportBreakdown}` +
      `?contentType=${contentType}&sourceLocale=${sourceLocale}` +
      `&targetLocale=${targetLocale}&estimationsId=${estimationsId}`;
    return lastValueFrom(this.http.get<FuzzyBreakdown>(url));
  }

  // ── Vendor details ─────────────────────────────────────────────────────

  /**
   * Get vendor details by vendor ID.
   */
  getVendorDetails(vendorId: string): Promise<any> {
    const url = `${PROXY_URL()}${ProxyEndPoints.getVendorDetails}/${vendorId}`;
    return lastValueFrom(this.http.get<any>(url));
  }

  // ── Contact AZ Team ────────────────────────────────────────────────────

  /**
   * Contact AZ team about a specific order/estimate.
   */
  contactAZT(
    order: VendorOrder,
    estimate: Estimate,
    message: string,
  ): Promise<boolean> {
    return lastValueFrom(
      this.http.post<boolean>(
        `${PROXY_URL()}${ProxyEndPoints.vendorOrdersContact}`,
        {
          order,
          message,
          estimate,
        },
      ),
    );
  }

  /**
   * Send a contact-AZ-team email notification.
   */
  contactAztMail(
    details: ContactAzTeamData,
    message: string,
    notificationType: ContactAzTeamNotificationType,
  ): Promise<boolean> {
    return lastValueFrom(
      this.http.post<boolean>(`${PROXY_URL()}${ProxyEndPoints.contactAzt}`, {
        details,
        message,
        notificationType,
      }),
    );
  }

  getXtmCostBreakdown(
    projectId: string,
    costId: string,
    costBreakdownId: string,
  ): Promise<FuzzyBreakdown> {
    const url = `${PROXY_URL()}${ProxyEndPoints.xtmCostBreakdown}?projectId=${projectId}&costId=${costId}&costBreakdownId=${costBreakdownId}`;
    return lastValueFrom(this.http.get<FuzzyBreakdown>(url));
  }

  getUnacknowledgedCount() {
    const url = `${PROXY_URL()}${ProxyEndPoints.unacknowledgedCount}`;
    return this.http.get<any>(url);
  }

  // ── Error handling ─────────────────────────────────────────────────────

  /**
   * Parse HTTP error responses into user-friendly messages.
   */
  handleErrors(err: { status: number; error: { error: string } }): string {
    const baseMessage = 'Sorry, there has been a problem';
    const serverErrorMessage: string = err.error?.error;

    if (err.status === 422 && serverErrorMessage) {
      switch (serverErrorMessage) {
        case 'Vendor estimation not found':
          return `${baseMessage}: Vendor estimation not found.`;
        case 'No purchase order id':
          return 'Could not authorize this order: No purchase order id.';
        case 'Vendor price is not valid':
          return 'Could not authorize this order: Vendor price is not valid.';
        case 'No vendor price':
          return 'Could not authorize this order: No vendor price.';
        case 'Dictionary not found':
          return `${baseMessage}: Please try again or contact support.`;
        case 'Order already cancelled':
          return 'Could not cancel job: Order already cancelled.';
        case 'Cannot cancel completed order':
          return 'Could not cancel job: A completed order cannot be cancelled.';
        default:
          return `${baseMessage}: Please try again or contact support.`;
      }
    } else if (err.status === 404 && err.error?.error === 'Order not found') {
      return `${baseMessage}: Order not found.`;
    }

    return `${baseMessage}: Please try again or contact support.`;
  }
}
